import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import '../../TefilotWidget.css';

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };
const PRAYER_ACCENT = { shacharit: '#1e3a5f', mincha: '#b8860b', maariv: '#374151' };

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
const getHebrewDay = () => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
};
const isOverrideActive = (days, dow, isRoshChodesh) =>
  (days || []).includes(dow) || (isRoshChodesh && (days || []).includes('roshChodesh'));
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

/* ── Horizontal auto-scroll hook (starts at right, scrolls left, seamless loop) ── */
const useHorizontalScroll = (ref, enabled, resetKey) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let intervalId = null;

    const startScroll = () => {
      if (el.scrollWidth <= el.clientWidth + 2) return false;
      el.scrollLeft = el.scrollWidth;
      intervalId = setInterval(() => {
        if (el.scrollWidth <= el.clientWidth + 2) return;
        el.scrollLeft -= 1;
        const half = Math.floor(el.scrollWidth / 2);
        if (el.scrollLeft <= 0) el.scrollLeft = half;
      }, 40);
      return true;
    };

    // ניסיון ראשון סינכרוני, fallback ב-RAF אם המידות עוד לא מוכנות
    if (!startScroll()) {
      const raf = requestAnimationFrame(startScroll);
      return () => { cancelAnimationFrame(raf); if (intervalId) clearInterval(intervalId); };
    }

    return () => { if (intervalId) clearInterval(intervalId); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, enabled, resetKey]);
};

/* ── Grid item for current panel ── */
const GridItem = ({ ct, isNext, showRuleMode, recurringDisplay, dow, isRoshChodesh, nowMin }) => {
  const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
  const isRecurring = ct.type === 'recurring';
  const activeOverride = ct._activeOverride;
  const rule = !isRecurring ? formatTimeRule(ct) : null;
  const inactive = ct.isActiveToday === false;
  const mainTime = activeOverride
    ? activeOverride.resolvedTime
    : isRecurring ? (ct.resolvedSlots?.[0] || null) : ct.resolvedTime;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
      padding: '12px 16px', borderRadius: '10px',
      backgroundColor: isNext ? '#eff6ff' : inactive ? '#f8fafc' : '#f8fafc',
      border: isNext ? '2px solid #1e3a5f' : inactive ? '1px solid #e2e8f0' : '1px solid #f1f5f9',
      boxShadow: isNext ? '0 2px 8px rgba(30,58,95,0.12)' : 'none',
      minWidth: '100px', position: 'relative',
    }}>
      {isNext && (
        <span style={{
          position: 'absolute', top: '-10px', right: '10px',
          fontSize: '10px', fontWeight: 700, color: '#fff',
          backgroundColor: '#1e3a5f', borderRadius: '10px', padding: '2px 8px',
          lineHeight: '16px',
        }}>המניין הבא</span>
      )}
      <span style={{ fontSize: '15px', fontWeight: 700, color: inactive ? '#78909c' : '#1e3a5f', textAlign: 'center' }}>{label}</span>

      {isRecurring && !activeOverride ? (
        /* Recurring: show start–end + mini slot chips */
        <>
          <span style={{
            fontSize: '22px', fontWeight: 800, color: inactive ? '#90a4ae' : '#1e3a5f',
            fontVariantNumeric: 'tabular-nums', direction: 'ltr', lineHeight: 1,
          }}>
            {ct.resolvedSlots?.[0]} — {ct.resolvedSlots?.[ct.resolvedSlots.length - 1]}
          </span>
          <span style={{ fontSize: '11px', color: '#166534', fontWeight: 600, backgroundColor: '#dcfce7', borderRadius: '20px', padding: '2px 8px' }}>
            כל {ct.interval} דק׳
          </span>
          {ct.resolvedSlots?.length > 0 && (() => {
            const slots = ct.resolvedSlots;
            // Build adjusted minutes — if a slot wraps past midnight its raw value drops,
            // so add 24h to keep the sequence monotonically increasing.
            const slotMins = slots.reduce((acc, slot, i) => {
              let m = toMinutes(slot);
              if (i > 0 && m < acc[i - 1]) m += 24 * 60;
              acc.push(m);
              return acc;
            }, []);
            const maxSlotMin = slotMins.length ? Math.max(...slotMins) : 0;
            // If any slot crossed midnight (>1440) and we're currently in early morning,
            // shift nowMin forward so it's in the same reference frame.
            const adjNow = (maxSlotMin > 1440 && nowMin < 360) ? nowMin + 1440 : nowMin;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center', direction: 'rtl', marginTop: '4px' }}>
                {slots.map((slot, i) => {
                  const isPast = slotMins[i] < adjNow;
                  const isNextSlot = isNext && !isPast && (i === 0 || slotMins[i - 1] < adjNow);
                  return (
                    <span key={i} style={{
                      fontSize: '16px', fontWeight: isNextSlot ? 800 : 600,
                      color: isPast ? '#cbd5e1' : isNextSlot ? '#fff' : '#475569',
                      fontVariantNumeric: 'tabular-nums',
                      backgroundColor: isNextSlot ? '#1e3a5f' : isPast ? 'transparent' : '#f8fafc',
                      border: isNextSlot ? '1px solid #1e3a5f' : isPast ? 'none' : '1px solid #e2e8f0',
                      borderRadius: '8px', padding: '4px 10px',
                      textDecoration: isPast ? 'line-through' : 'none',
                    }}>{slot}</span>
                  );
                })}
              </div>
            );
          })()}
        </>
      ) : (
        /* Fixed / relative / override */
        <>
          <span style={{
            fontSize: '38px', fontWeight: 800,
            color: inactive ? '#90a4ae' : activeOverride ? '#166534' : '#1e3a5f',
            fontVariantNumeric: 'tabular-nums', direction: 'ltr', lineHeight: 1,
          }}>{mainTime || '—'}</span>
          {!showRuleMode && rule && (
            <span style={{ fontSize: '11px', color: inactive ? '#90a4ae' : '#64748b', fontWeight: 500, textAlign: 'center' }}>
              {rule}
            </span>
          )}
        </>
      )}

      {ct.daysLabel && (
        <span style={{
          position: 'absolute', top: '-10px', left: '10px',
          fontSize: '11px', fontWeight: 700, color: '#1d4ed8',
          backgroundColor: '#dbeafe', border: '1px solid #93c5fd',
          borderRadius: '10px', padding: '2px 8px', lineHeight: '16px',
          whiteSpace: 'nowrap',
        }}>
          {ct.daysLabel}
        </span>
      )}
      {(ct.resolvedOverrides || []).map((ov, oi) => {
        const active = isOverrideActive(ov.days, dow, isRoshChodesh);
        const dayLabels = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
        return (
          <div key={oi} style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: active ? '2px 6px' : 0, borderRadius: '6px',
            backgroundColor: active ? '#f0fdf4' : 'transparent',
            border: active ? '1px solid #bbf7d0' : 'none',
          }}>
            {active && <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16a34a', display: 'inline-block' }} />}
            <span style={{ fontSize: '11px', color: active ? '#15803d' : '#cbd5e1', fontWeight: active ? 600 : 400 }}>{dayLabels}</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: active ? '#15803d' : '#cbd5e1', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>{ov.resolvedTime}</span>
          </div>
        );
      })}
    </div>
  );
};

/* ── Horizontal strip for non-current panels ── */
const HorizontalStrip = ({ panelKey, accent, items, dow, isRoshChodesh, containerWidth, recurringDisplay }) => {
  const scrollRef = useRef(null);
  useHorizontalScroll(scrollRef, true, containerWidth);

  const itemsRow = items.flatMap((ct, idx) => {
    const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
    const activeOverride = ct._activeOverride;
    const isRecurring = ct.type === 'recurring';
    const isLast = idx === items.length - 1;

    // full mode: expand recurring into one chip per slot
    if (isRecurring && recurringDisplay === 'full' && !activeOverride && ct.resolvedSlots?.length > 0) {
      return ct.resolvedSlots.map((slot, si) => (
        <div key={`${ct.id || idx}-${si}`} style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          padding: '4px 10px',
          borderLeft: (si < ct.resolvedSlots.length - 1 || !isLast) ? '1px solid #e2e8f0' : 'none',
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {si === 0 && <span style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>{label}</span>}
          <span style={{
            fontSize: '20px', fontWeight: 800, color: '#1e3a5f',
            fontVariantNumeric: 'tabular-nums', direction: 'ltr',
          }}>{slot}</span>
        </div>
      ));
    }

    const mainTime = activeOverride
      ? activeOverride.resolvedTime
      : isRecurring ? (ct.resolvedSlots?.[0] || null) : ct.resolvedTime;
    const activeOv = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days, dow, isRoshChodesh));

    return [(
      <div key={ct.id || idx} style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '4px 14px',
        borderLeft: !isLast ? '1px solid #e2e8f0' : 'none',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        <span style={{ fontSize: '16px', fontWeight: 600, color: '#475569' }}>{label}</span>
        <span style={{
          fontSize: '20px', fontWeight: 800, color: activeOv ? '#166534' : '#1e3a5f',
          fontVariantNumeric: 'tabular-nums', direction: 'ltr',
        }}>{mainTime || '—'}</span>
        {ct.daysLabel && (
          <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500 }}>{ct.daysLabel}</span>
        )}
        {isRecurring && ct.resolvedSlots?.length > 1 && !activeOverride && (
          <span style={{ fontSize: '14px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>
            — {ct.resolvedSlots[ct.resolvedSlots.length - 1]}
          </span>
        )}
      </div>
    )];
  });

  return (
    <div style={{ borderTop: '1px solid #f1f5f9', minWidth: 0, overflow: 'hidden' }}>
      <div style={{ padding: '2px 20px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: accent, flexShrink: 0 }} />
        <span style={{ fontSize: '13px', fontWeight: 700, color: accent, letterSpacing: '0.05em' }}>{PRAYER_LABEL[panelKey]}</span>
      </div>
      <div ref={scrollRef} style={{ overflow: 'hidden', direction: 'ltr' }}>
        <div style={{ display: 'flex', flexDirection: 'row-reverse', padding: '4px 20px 6px', width: 'max-content' }}>
          {itemsRow}
          {/* duplicate for seamless scroll */}
          <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'row-reverse' }}>{itemsRow}</div>
        </div>
      </div>
    </div>
  );
};

/* ── Main ── */
const Focus = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', showRuleMode = false, highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const gridRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(null);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => { const w = el.offsetWidth; if (w > 0) setGridWidth(w); };
    measure();
    const raf = requestAnimationFrame(measure);
    document.fonts.ready.then(() => requestAnimationFrame(measure));
    const ro = new ResizeObserver(() => { const w = el.offsetWidth; if (w > 0) setGridWidth(w); });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  // Re-run when data arrives — on first load processedCustomTimes is empty during mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedCustomTimes?.length]);

  const dow = now.getDay();
  const hebrewDay = getHebrewDay();
  const isRoshChodesh = hebrewDay === 1 || hebrewDay === 30;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const adjNowMin = nowMin < 180 ? nowMin + 1440 : nowMin;

  const { groups, currentKey } = useMemo(() => {
    if (!processedCustomTimes?.length) return { groups: {}, currentKey: 'shacharit' };
    const grp = { shacharit: [], mincha: [], maariv: [] };
    for (const ct of processedCustomTimes) {
      const key = ct.prayer;
      if (!grp[key]) continue;
      const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days, dow, isRoshChodesh));
      let times = [];
      if (ct.type === 'recurring') {
        times = activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0);
      } else {
        const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime);
        if (m >= 0) times = [m];
      }
      grp[key].push({ ...ct, _times: times, _activeOverride: activeOverride });
    }
    // Sort each group by effective time for today
    for (const k of PRAYER_ORDER) {
      grp[k].sort((a, b) => {
        const tA = a._times[0] ?? Infinity;
        const tB = b._times[0] ?? Infinity;
        return tA - tB;
      });
    }
    const ranges = {};
    for (const k of PRAYER_ORDER) { const t = grp[k].filter(e => e.isActiveToday !== false).flatMap(e => e._times); if (t.length) ranges[k] = { min: Math.min(...t), max: Math.max(...t) }; }
    const activeKeys = PRAYER_ORDER.filter(k => ranges[k]);
    let current = activeKeys[activeKeys.length - 1] || 'maariv';
    // Once all prayers of a group have passed, move to the next group.
    // adjNowMin treats 00:00–02:59 as "still last night" to avoid flipping to shacharit at midnight.
    for (let i = 0; i < activeKeys.length; i++) {
      if (adjNowMin <= ranges[activeKeys[i]].max + 5) { current = activeKeys[i]; break; }
    }
    if (activeKeys.length === 1) current = activeKeys[0];
    if (!activeKeys.length) current = adjNowMin < 720 ? 'shacharit' : adjNowMin < 1020 ? 'mincha' : 'maariv';
    return { groups: grp, currentKey: current };
  }, [processedCustomTimes, dow, isRoshChodesh, adjNowMin]);

  // Find the next upcoming prayer in the current group
  const nextIdx = useMemo(() => {
    const items = groups[currentKey] || [];
    let bestIdx = -1;
    let bestMin = Infinity;
    for (let i = 0; i < items.length; i++) {
      const ct = items[i];
      if (ct.isActiveToday === false) continue;
      const times = ct._times || [];
      for (const t of times) {
        if (t >= adjNowMin && t < bestMin) { bestMin = t; bestIdx = i; }
      }
    }
    return bestIdx;
  }, [groups, currentKey, adjNowMin]);

  if (loading) return <div style={styles.state}>טוען זמני תפילה...</div>;
  if (error) return <div style={{ ...styles.state, color: '#D32F2F' }}>{error}</div>;
  if (!processedCustomTimes?.length) return <div style={styles.state}>אין זמני תפילה</div>;

  const otherPanels = PRAYER_ORDER.filter(k => k !== currentKey && groups[k]?.length > 0);

  return (
    <div className="focus-outer" style={{ height: '100%', width: '100%', maxWidth: gridWidth ? gridWidth + 40 : undefined, direction: 'rtl', padding: '20px', boxSizing: 'border-box', overflow: 'visible', fontFamily: 'inherit', display: 'flex', flexDirection: 'column' }}>
      {showTitle && (
        <div style={{ paddingBottom: '16px', marginBottom: '16px', borderBottom: '2px solid #e5ddd3' }}>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1e3a5f' }}>{title}</h2>
        </div>
      )}

      {/* Current panel header */}
      {groups[currentKey]?.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ padding: '0 0 4px', borderRight: `3px solid ${PRAYER_ACCENT[currentKey]}`, paddingRight: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: PRAYER_ACCENT[currentKey], letterSpacing: '0.05em' }}>{PRAYER_LABEL[currentKey]}</span>
          </div>

          {/* Grid layout for current times */}
          <div ref={gridRef} style={{
            display: 'flex', gap: '10px',
            width: 'max-content',
          }}>
            {groups[currentKey].map((ct, idx) => (
              <GridItem key={ct.id || idx} ct={ct} isNext={highlightNextMinyan && idx === nextIdx} showRuleMode={showRuleMode} recurringDisplay={recurringDisplay} dow={dow} isRoshChodesh={isRoshChodesh} nowMin={nowMin} />
            ))}
          </div>
        </div>
      )}

      {/* Spacer to push strips to bottom */}
      <div style={{ flex: 1 }} />

      {/* Horizontal strips for other panels at the bottom */}
      {otherPanels.map(k => (
        <HorizontalStrip key={k} panelKey={k} accent={PRAYER_ACCENT[k]} items={groups[k]} dow={dow} isRoshChodesh={isRoshChodesh} containerWidth={gridWidth} recurringDisplay={recurringDisplay} />
      ))}
    </div>
  );
};

const styles = { state: { width: '100%', padding: '20px', textAlign: 'center', direction: 'rtl', fontSize: '18px', color: '#64748b' } };

export default Focus;
